import React, { useRef, useEffect, useState, useCallback, useMemo } from "react"
import L from "leaflet"
import { getSliceColor, PLACEHOLDER_SLOTS } from "../shared/SpinWheel"
import { initMap, createMapMarkers, markerPopup, escHtml, addUserMarker } from "../../helpers/mapStyles"
import EmptyState from "./EmptyState"
import ColorsSection from "./ColorsSection"
import useRodgColors from "./hooks/useRodgColors"
import useRodgSelections from "./hooks/useRodgSelections"
import useMapRecenter from "../../hooks/useMapRecenter"
import { loadDoneSelections, loadWinner, loadParkIds, loadUserCoords, markDone } from "./helpers/storage"
import usePersistedState from "../../hooks/usePersistedState"

const DISNEYLAND = [33.8121, -117.919]
const DEFAULT_ZOOM = 15
const DONE_COLOR = "#999"
const ALL_ENTITIES_COLOR = "#8B5CF6"
const FIT_PADDING = { paddingBottomRight: [0, 48] }
const BOUNDS_PAD = 0.15

const itemPopup = (item) => {
  if (item.wait_minutes) return markerPopup(item.name, item.wait_minutes)
  if (item.meta) return `<strong>${escHtml(item.name)}</strong><br>${escHtml(item.meta)}`
  return markerPopup(item.name, null)
}

const activePopup = (item) =>
  `${itemPopup(item)}<br><button class="rodg-map-done-btn" data-name="${escHtml(item.name)}">Done ✓</button>`

const entityPopup = (entity) => {
  const lat = entity.location?.latitude ?? ""
  const lng = entity.location?.longitude ?? ""
  const statusText =
    !entity.wait_minutes && entity.status ? (entity.status === "OPERATING" ? "Open" : entity.status) : null
  return [
    markerPopup(entity.name, entity.wait_minutes),
    entity.park_name && `<span class="rodg-map-park-name">${escHtml(entity.park_name)}</span>`,
    statusText,
    `<button class="rodg-map-done-btn" data-name="${escHtml(entity.name)}" data-lat="${lat}" data-lng="${lng}">Done ✓</button>`,
  ]
    .filter(Boolean)
    .join("<br>")
}

const toMapItem = (entity) => ({
  name: entity.name,
  lat: entity.location?.latitude ?? null,
  lng: entity.location?.longitude ?? null,
  wait_minutes: entity.wait_minutes ?? null,
  _entity: entity,
})

const Map = () => {
  const mapRef = useRef(null)
  const { mapInstanceRef, boundsRef, handleRecenter } = useMapRecenter(FIT_PADDING)
  const { items, removeItem, visibleItems, parkEntities, entitiesLoading, filterByName } = useRodgSelections()
  const { customColors, handleColorChange, handleResetAllColors } = useRodgColors({
    resetOnMount: items.length === 0,
  })
  const [doneItems, setDoneItems] = useState(loadDoneSelections)
  const [winnerName] = useState(loadWinner)
  const [showAll, setShowAll] = usePersistedState("map_show_all", false, { prefix: "rodg_" })
  const [userCoords, setUserCoords] = useState(loadUserCoords)
  const [hasParkIds] = useState(() => loadParkIds().length > 0)

  useEffect(() => {
    const onLocationChange = (e) => setUserCoords(e.detail?.coords ?? null)
    document.addEventListener("rodg-location-change", onLocationChange)
    return () => document.removeEventListener("rodg-location-change", onLocationChange)
  }, [])

  const handleToggleAll = useCallback(() => setShowAll((prev) => !prev), [setShowAll])

  const visibleDoneItems = useMemo(() => filterByName(doneItems), [doneItems, filterByName])
  const hasVisibleActive = visibleItems.length > 0
  const visibleEmpty = visibleItems.length === 0 && visibleDoneItems.length === 0
  const choices = hasVisibleActive ? visibleItems.map((item) => item.name) : PLACEHOLDER_SLOTS

  useEffect(() => {
    const el = mapRef.current
    if (!el) return

    const map = initMap(L, el)
    mapInstanceRef.current = map
    const fitTo = (bounds) => {
      boundsRef.current = bounds.pad(BOUNDS_PAD)
      map.fitBounds(boundsRef.current, FIT_PADDING)
    }
    const showDefaultView = () => map.setView(DISNEYLAND, DEFAULT_ZOOM)

    if (visibleEmpty && !showAll) {
      if (parkEntities?.length > 0) {
        fitTo(L.latLngBounds(parkEntities.map((e) => [e.location.latitude, e.location.longitude])))
      } else {
        showDefaultView()
      }
      return () => map.remove()
    }

    // All-entities layer (added first so selections render on top)
    let allEntityMarkers = []
    if (showAll && parkEntities) {
      const selectionNames = new Set(visibleItems.concat(visibleDoneItems).map((s) => s.name.toLowerCase()))
      const filtered = parkEntities.filter((e) => !selectionNames.has(e.name.toLowerCase()))
      allEntityMarkers = createMapMarkers(L, map, filtered.map(toMapItem), {
        colorFn: () => ALL_ENTITIES_COLOR,
        popupFn: (item) => entityPopup(item._entity),
      })
    }

    const activeMarkers = createMapMarkers(L, map, visibleItems, {
      colorFn: (_item, i) => getSliceColor(i, customColors),
      winnerName,
      labelFn: (_item, i) => i + 1,
      popupFn: activePopup,
    })
    const doneMarkers = createMapMarkers(L, map, visibleDoneItems, { colorFn: () => DONE_COLOR })

    const allMarkers = allEntityMarkers.concat(activeMarkers, doneMarkers)
    if (userCoords) allMarkers.push(addUserMarker(L, map, userCoords))
    if (allMarkers.length > 0) {
      fitTo(L.featureGroup(allMarkers).getBounds())
    } else if (visibleEmpty) {
      showDefaultView()
    }

    const onDoneClick = (e) => {
      const btn = e.target.closest(".rodg-map-done-btn")
      if (!btn) return
      const name = btn.dataset.name
      const lat = parseFloat(btn.dataset.lat)
      const lng = parseFloat(btn.dataset.lng)
      const fallback = !isNaN(lat) && !isNaN(lng) ? { lat, lng } : null
      if (markDone(name, fallback)) {
        removeItem(name)
        setDoneItems(loadDoneSelections)
      }
    }
    el.addEventListener("click", onDoneClick)

    return () => {
      el.removeEventListener("click", onDoneClick)
      map.remove()
    }
  }, [
    visibleItems,
    visibleDoneItems,
    winnerName,
    visibleEmpty,
    customColors,
    showAll,
    parkEntities,
    userCoords,
    removeItem,
  ])

  return (
    <>
      {visibleEmpty && !showAll && <EmptyState context="map" />}
      <div className="map-wrapper">
        <div ref={mapRef} className="rodg-map-container" />
        {hasParkIds && (
          <button
            className={`btn-base btn-secondary rodg-map-toggle-btn${showAll ? " active" : ""}`}
            onClick={handleToggleAll}
            disabled={entitiesLoading}
          >
            {entitiesLoading ? "Loading..." : "Show All"}
          </button>
        )}
        {(!visibleEmpty || showAll) && (
          <button className="map-recenter-btn" onClick={handleRecenter} title="Recenter map">
            Recenter
          </button>
        )}
      </div>
      <ColorsSection
        className="rodg-map-colors"
        choices={choices}
        customColors={customColors}
        onColorChange={handleColorChange}
        onResetAll={handleResetAllColors}
      />
    </>
  )
}

export default Map
